/**
 * MeetingTasksTab — 「待办」页签：待确认候选 + 已确认正式任务。
 *
 * 候选确认按钮在纪要未经本人确认前禁用（S11 确认门禁）——候选待办来自尚未
 * 确认的 AI 草稿，确认前不允许它们变成正式任务。
 */

import { List, Space, Tag, Typography } from "antd";
import type { Task } from "../../types";
import type {
  MeetingAnalysisState,
  MeetingTaskCandidate,
  MeetingTaskCandidateDecision,
} from "../../types/meeting";
import EmptyState from "../common/EmptyState";
import MeetingAiActionStatus from "./MeetingAiActionStatus";
import MeetingTaskCandidates from "./MeetingTaskCandidates";

const { Text, Title } = Typography;

const TASK_PRIORITY_TAG: Record<string, { label: string; color: string }> = {
  urgent: { label: "紧急", color: "red" },
  high: { label: "重要", color: "orange" },
  normal: { label: "常规", color: "blue" },
  low: { label: "暂缓", color: "default" },
};

const TASK_STATUS_LABEL: Record<string, string> = {
  pending: "待处理",
  in_progress: "进行中",
  pending_condition: "待条件",
  submitted: "已提交",
  completed: "已完成",
  cancelled: "已取消",
};

interface MeetingTasksTabProps {
  meetingId: string;
  candidates: MeetingTaskCandidate[];
  candidatesLoading: boolean;
  tasks: Task[];
  confirmed: boolean;
  confirmSubmitting: boolean;
  confirmError: string | null;
  onConfirmCandidates: (decisions: MeetingTaskCandidateDecision[]) => void;
  extractState: MeetingAnalysisState;
  extractErrorMessage: string | null;
  extractCompletedAt: string | null;
  extractSubmitting: boolean;
  onSubmitExtract: () => void;
  hasTranscript: boolean;
  noTranscriptReason: string;
}

export default function MeetingTasksTab({
  meetingId,
  candidates,
  candidatesLoading,
  tasks,
  confirmed,
  confirmSubmitting,
  confirmError,
  onConfirmCandidates,
  extractState,
  extractErrorMessage,
  extractCompletedAt,
  extractSubmitting,
  onSubmitExtract,
  hasTranscript,
  noTranscriptReason,
}: MeetingTasksTabProps) {
  return (
    <div>
      <MeetingAiActionStatus
        action="extract-tasks"
        state={extractState}
        errorMessage={extractErrorMessage}
        completedAt={extractCompletedAt}
        hasPreviousResult={candidates.length > 0 || tasks.length > 0}
        onSubmit={onSubmitExtract}
        submitting={extractSubmitting}
        blocked={!hasTranscript}
        blockedReason={!hasTranscript ? noTranscriptReason : undefined}
      />

      <Title level={5}>待确认的待办</Title>
      <MeetingTaskCandidates
        meetingId={meetingId}
        candidates={candidates}
        isLoading={candidatesLoading}
        submitting={confirmSubmitting}
        submitError={confirmError}
        onConfirm={onConfirmCandidates}
        confirmed={confirmed}
      />

      <Title level={5} style={{ marginTop: "var(--jerry-sp-5)" }}>
        已确认的任务
      </Title>
      {tasks.length > 0 ? (
        <List
          itemLayout="vertical"
          dataSource={tasks}
          renderItem={(t) => {
            const pt = TASK_PRIORITY_TAG[t.priority] ?? TASK_PRIORITY_TAG.normal;
            return (
              <List.Item key={t.id}>
                <Space orientation="vertical" size={4} style={{ width: "100%" }}>
                  <Space wrap>
                    <Tag color={pt.color}>{pt.label}</Tag>
                    <Tag>{TASK_STATUS_LABEL[t.status] ?? t.status}</Tag>
                  </Space>
                  <Text strong>{t.title}</Text>
                  {t.notes && (
                    <Text
                      type="secondary"
                      style={{
                        whiteSpace: "pre-wrap",
                        fontSize: 12,
                        background: "var(--jerry-gray-100)",
                        padding: "8px 10px",
                        borderRadius: 4,
                      }}
                    >
                      {t.notes}
                    </Text>
                  )}
                </Space>
              </List.Item>
            );
          }}
        />
      ) : (
        <EmptyState
          title="暂无已确认的任务"
          description="确认上方的待办之后，它们会成为正式任务，出现在任务中心。"
        />
      )}
    </div>
  );
}
